import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight, Phone, Mail, MapPin, Clock, MessageCircle, Loader2, Check, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { api } from "../lib/api";
import { company } from "../data/site";
import { toast } from "sonner";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/contact", form);
      setSuccess(true);
      toast.success(r.data.message || "Mesajınız alındı.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bir sorun oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-0 bg-[#FAFAFA]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#888] mb-8">
          <Link to="/" className="hover:text-[#B8860B]">Ana Sayfa</Link>
          <ChevronRight size={12} />
          <span className="text-[#1E1E1E]">İletişim</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 pb-24">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#B8860B] mb-4">
              İletişim
            </div>
            <h1 className="font-serif-display text-3xl md:text-4xl text-[#1E1E1E] leading-tight">
              İletişim
            </h1>
            <p className="mt-4 text-[14px] text-[#555] leading-relaxed max-w-sm">
              Showroom'umuza uğrayın, koleksiyonları gerçek ölçekte inceleyin.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { icon: MapPin, label: "Adres", value: company.address, href: `https://www.google.com/maps?q=${encodeURIComponent(company.address)}` },
                { icon: Phone, label: "Telefon", value: company.phone, href: `tel:${company.phoneRaw}` },
                { icon: MessageCircle, label: "WhatsApp", value: company.phone, href: company.whatsapp },
                { icon: Instagram, label: "Instagram", value: "@seramikpark.com", href: company.social.instagram },
                { icon: Mail, label: "E-posta", value: company.email, href: `mailto:${company.email}` },
              ].map(({ icon: Icon, label, value, href }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="flex items-start gap-4 group"
                  data-testid={`contact-info-${label.toLowerCase()}`}
                >
                  <div className="w-11 h-11 border border-[#B8860B]/30 grid place-items-center text-[#B8860B] group-hover:bg-[#B8860B] group-hover:text-white transition-colors shrink-0">
                    <Icon size={16} />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-[#888]">{label}</div>
                    <div className="text-[15px] text-[#1E1E1E] mt-0.5">{value}</div>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-10 p-6 bg-white border border-black/5">
              <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#B8860B] mb-4">
                <Clock size={12} /> Çalışma Saatleri
              </div>
              <ul className="space-y-2 text-[13px]">
                {company.hours.map((h) => (
                  <li key={h.day} className="flex justify-between text-[#444]">
                    <span>{h.day}</span>
                    <span className="text-[#1E1E1E] font-medium">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6">
              <div className="text-[10px] tracking-widest uppercase text-[#888] mb-4">
                Sosyal Medya
              </div>
              <div className="flex items-center gap-3">
                {[
                  { icon: Instagram, href: company.social.instagram, label: "Instagram" },
                  { icon: Facebook, href: company.social.facebook, label: "Facebook" },
                  { icon: Linkedin, href: company.social.linkedin, label: "LinkedIn" },
                  { icon: Youtube, href: company.social.youtube, label: "YouTube" },
                ].map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    data-testid={`contact-social-${label.toLowerCase()}`}
                    className="w-11 h-11 grid place-items-center border border-[#1E1E1E]/15 text-[#1E1E1E] hover:border-[#B8860B] hover:text-[#B8860B] transition-colors"
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {success ? (
              <div data-testid="contact-success" className="p-12 border border-black/5 bg-white text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#B8860B]/10 border border-[#B8860B]/30 grid place-items-center text-[#B8860B]">
                  <Check size={28} />
                </div>
                <h3 className="mt-6 font-serif-display text-3xl text-[#1E1E1E]">Mesajınız iletildi.</h3>
                <p className="mt-3 text-[#666]">En kısa sürede size ulaşacağız.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="bg-white p-8 md:p-12 border border-black/5">
                <div className="text-[10px] tracking-widest uppercase text-[#B8860B] mb-4">
                  Bize yazın
                </div>
                <h3 className="font-serif-display text-2xl text-[#1E1E1E] mb-6">
                  Bize yazın
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="sp-label">Ad Soyad *</label>
                    <input required data-testid="c-name" className="sp-input" value={form.name} onChange={on("name")} />
                  </div>
                  <div>
                    <label className="sp-label">E-posta *</label>
                    <input required type="email" data-testid="c-email" className="sp-input" value={form.email} onChange={on("email")} />
                  </div>
                  <div>
                    <label className="sp-label">Telefon</label>
                    <input data-testid="c-phone" className="sp-input" value={form.phone} onChange={on("phone")} />
                  </div>
                  <div>
                    <label className="sp-label">Konu</label>
                    <input data-testid="c-subject" className="sp-input" value={form.subject} onChange={on("subject")} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="sp-label">Mesajınız *</label>
                    <textarea required data-testid="c-msg" rows={5} className="sp-input resize-none" value={form.message} onChange={on("message")} />
                  </div>
                </div>
                <button data-testid="c-submit" disabled={loading} className="btn-gold mt-10 disabled:opacity-60">
                  {loading ? <><Loader2 size={14} className="animate-spin" /> Gönderiliyor</> : <>Gönder <ArrowRight size={14} /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="w-full h-[520px] bg-[#F5F5F5] border-t border-black/5" data-testid="contact-map">
        <iframe
          src={company.mapEmbed}
          title="SeraPark harita"
          width="100%"
          height="100%"
          style={{ border: 0, filter: "grayscale(0.35)" }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  );
}
